import React, { useEffect, useState } from "react";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { BadgeCheck, ArrowUpRight } from "lucide-react";
import { TELEGRAM_URL } from "@/config";
import { trackSubscribe } from "@/lib/track";

const testimonials = [
    {
        img: "/testimonial-1.jpg",
        crop: false,
        name: "Community Member",
        location: "Malaysia, MY",
        caption:
            "Tq MZ untuk hari ni — dah dapat flip 100% hari ni! Grateful for the guidance.",
    },
    {
        img: "/testimonial-4.png",
        crop: false,
        name: "Community Member",
        location: "Malaysia, MY",
        caption:
            "Followed the signals & MTL Indicator — flipped my bonus account with discipline after 7 months.",
    },
    {
        img: "/testimonial-3.jpg",
        crop: false,
        name: "Community Member",
        location: "Malaysia, MY",
        caption:
            "Profit TP 🔥 — staying patient and trusting the process pays off.",
    },
    {
        img: "/testimonial-2.jpg",
        crop: false,
        name: "Community Member",
        location: "Malaysia, MY",
        caption:
            "Alhamdulillah — comeback selepas 3 bulan tak trade, terus profit. Masih banyak nak belajar dari MZ.",
    },
];

const TestimonialsSection = () => {
    const [api, setApi] = useState(null);
    const [current, setCurrent] = useState(0);
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!api) return;
        setCount(api.scrollSnapList().length);
        setCurrent(api.selectedScrollSnap());
        const onSelect = () => setCurrent(api.selectedScrollSnap());
        api.on("select", onSelect);
        return () => {
            api.off("select", onSelect);
        };
    }, [api]);

    // Autoplay
    useEffect(() => {
        if (!api) return undefined;
        const id = setInterval(() => {
            if (api.canScrollNext()) api.scrollNext();
            else api.scrollTo(0);
        }, 4500);
        return () => clearInterval(id);
    }, [api]);

    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="bg-white py-20 md:py-28 overflow-hidden"
        >
            <div className="px-6 md:px-12 lg:px-24">
                <div className="max-w-3xl">
                    <span className="inline-block text-xs sm:text-sm font-semibold tracking-widest uppercase text-[#0077b6]">
                        Real Members, Real Words
                    </span>
                    <h2
                        data-testid="testimonials-heading"
                        className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-black"
                    >
                        What members are saying
                    </h2>
                    <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                        Real messages from our members — shared with permission.
                        Swipe through their stories. Every trader's journey is
                        different, but the guidance and community stay the same.
                    </p>
                </div>

                <div className="mt-12">
                    <Carousel
                        setApi={setApi}
                        opts={{ align: "start", loop: true }}
                        className="w-full"
                        data-testid="testimonials-carousel"
                    >
                        <CarouselContent className="-ml-4">
                            {testimonials.map((t, i) => (
                                <CarouselItem
                                    key={i}
                                    data-testid={`testimonial-slide-${i}`}
                                    className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3"
                                >
                                    <div className="group rounded-3xl border border-gray-200 bg-[#F2F2F2] p-5 h-full flex flex-col">
                                        <div className="relative rounded-2xl overflow-hidden bg-black">
                                            <div
                                                className={
                                                    t.crop
                                                        ? "aspect-[38/74] overflow-hidden"
                                                        : "aspect-[38/74] overflow-hidden"
                                                }
                                            >
                                                <img
                                                    src={t.img}
                                                    alt={`Testimonial from ${t.name}`}
                                                    data-testid={`testimonial-img-${i}`}
                                                    loading="lazy"
                                                    className={`h-full w-full object-cover object-center ${
                                                        t.crop
                                                            ? "scale-[1.5]"
                                                            : "scale-[1.02]"
                                                    }`}
                                                />
                                            </div>
                                        </div>

                                        <div className="mt-5 flex-1 flex flex-col">
                                            <p className="text-sm sm:text-base text-gray-700 leading-relaxed flex-1">
                                                "{t.caption}"
                                            </p>
                                            <div className="mt-4 flex items-center gap-2 pt-4 border-t border-gray-200/70">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0077b6] text-white text-xs font-display font-bold">
                                                    {t.name
                                                        .split(" ")
                                                        .map((w) => w[0])
                                                        .join("")
                                                        .slice(0, 2)}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-display text-sm font-semibold text-black">
                                                            {t.name}
                                                        </span>
                                                        <BadgeCheck className="h-3.5 w-3.5 text-[#0077b6]" />
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {t.location}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>

                        <CarouselPrevious
                            data-testid="testimonials-prev"
                            className="hidden sm:flex -left-3 lg:-left-5 bg-white border-gray-200 text-black hover:bg-[#0077b6] hover:text-white"
                        />
                        <CarouselNext
                            data-testid="testimonials-next"
                            className="hidden sm:flex -right-3 lg:-right-5 bg-white border-gray-200 text-black hover:bg-[#0077b6] hover:text-white"
                        />
                    </Carousel>

                    {/* Dots */}
                    <div className="mt-8 flex items-center justify-center gap-2">
                        {Array.from({ length: count }).map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                aria-label={`Go to testimonial ${i + 1}`}
                                data-testid={`testimonials-dot-${i}`}
                                onClick={() => api?.scrollTo(i)}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    current === i
                                        ? "w-7 bg-[#0077b6]"
                                        : "w-2 bg-gray-300 hover:bg-gray-400"
                                }`}
                            />
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col items-center gap-3 text-center">
                        <a
                            href={TELEGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="testimonials-cta"
                            onClick={trackSubscribe}
                            className="btn-primary inline-flex items-center gap-2 bg-[#0077b6] hover:bg-[#005f92] text-white font-semibold rounded-xl px-7 py-3.5 text-sm transition-colors"
                        >
                            Join the community
                            <ArrowUpRight className="h-4 w-4" />
                        </a>
                        <p className="text-xs text-gray-400 max-w-xl leading-relaxed">
                            Individual results vary and are not guaranteed.
                            Trading involves risk. These messages reflect
                            members' personal experiences.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;
